import { useState } from "react";
import type { BuyerSubmissionDetailDto } from "@/api/buyers";
import { useSendHistory } from "@/hooks/buyers/useSendHistory";
import { DetailSection } from "../detailBits";
import { CollapsibleCard } from "../introCards/CollapsibleCard";
import { introCardCopy } from "../introCards/introCardCopy";
import { ExpandAllControl } from "../introCards/SentFold";
import { IntroEmailPanel } from "../introEmail/IntroEmailPanel";
import { useIntroEmailState } from "../introEmail/useIntroEmailState";
import { SendHistory } from "../sendHistory/SendHistory";

/**
 * The Buyer intro tab — the short email to the BUYER that precedes the package.
 *
 * <p><b>A cheap question asked before an expensive one.</b> It asks the buyer
 * whether this listing is worth taking to its broker at all; until it comes
 * back there is nothing to introduce.</p>
 *
 * <p>🔴 <b>The broker intro is a TAB of its own (D23).</b> The two intros used
 * to share this tab as two stacked cards (K1), on the argument that they are
 * the same step of the loop from two sides. The ruling reversed that: the
 * broker side grew a readiness strip, three channels and follow-ups — a whole
 * workflow aimed at an outside party — and stacking it under a buyer card put
 * the wrong audience one scroll away. The key `intro-email` stays, so saved
 * `?tab=intro-email` links still land here; the broker side is
 * <c>SubmissionBrokerIntroTab</c> on `broker-intro`.</p>
 *
 * <p>🔴 <b>The send moves no entitlement count.</b> That is what keeps it off
 * the package tab: the package's send moves <c>PackageSentAtUtc</c> and with it
 * what the specialist has earned, and this deliberately moves nothing — pinned
 * server-side by <c>Sending_an_intro_email_moves_no_entitlement_count</c>.</p>
 *
 * <p>🔴 <b><c>canWrite</c> arrives as a prop and the panel computes none of its
 * own.</b> The page resolves the module permission and the row's own ownership
 * answer once; a panel that gates on the row's bare <c>canEdit</c> offers writes
 * the server refuses, and the specialist cannot tell that from a broken
 * button.</p>
 *
 * <p><b>The draft sits above its own history, and the order is the point.</b>
 * The panel holds the one thing being worked on — a draft nobody has received —
 * and the history below holds every email that actually reached the buyer. A
 * draft earns no row, including the one the Redraft undo keeps recoverable: the
 * history answers "what did they get", and a row for something nobody got would
 * make it answer that wrongly.</p>
 *
 * <p><b>Collapsible (D2, D4, D5, J6, J8).</b> The card starts open when nothing
 * has been sent for this submission and collapsed when something has; after a
 * send taken here it folds. A refetch never opens or closes it. The history
 * sits, unchanged, in one collapsed "Send history (n)" card beneath it.</p>
 */
type CardKey = "intro" | "history";

export function SubmissionIntroEmailTab({
  s,
  canWrite,
}: {
  s: BuyerSubmissionDetailDto;
  canWrite: boolean;
}) {
  const intro = useIntroEmailState(s.id);
  const history = useSendHistory(s.id);
  const sentCount = history.data?.introEmails.length ?? 0;

  // D4 — decided once, when the card's read first answers (or fails).
  const [open, setOpen] = useState<Record<CardKey, boolean> | null>(null);
  const [acted, setActed] = useState(false);
  const anySend = Boolean(intro.data?.sentAtUtc) || sentCount > 0;

  if (open === null && (intro.data || intro.isError)) {
    setOpen({ intro: !anySend, history: false });
  }
  // J6 — fold after a send taken in this tab, once the send reads back.
  if (open && acted && anySend) {
    setOpen({ ...open, intro: false });
    setActed(false);
  }

  const isOpen = (key: CardKey) => open?.[key] ?? false;
  const setCard = (key: CardKey) => (next: boolean) =>
    setOpen((prev) => ({ ...(prev ?? { intro: false, history: false }), [key]: next }));

  return (
    <div className="grid gap-space-5">
      <ExpandAllControl
        anyOpen={isOpen("intro") || isOpen("history")}
        onExpandAll={() => setOpen({ intro: true, history: true })}
        onCollapseAll={() => setOpen({ intro: false, history: false })}
        testId="buyer-intro-expand-all"
      />

      <IntroEmailPanel
        submissionId={s.id}
        canWrite={canWrite}
        open={isOpen("intro")}
        onOpenChange={setCard("intro")}
        onActed={() => setActed(true)}
      />

      <CollapsibleCard
        open={isOpen("history")}
        onOpenChange={setCard("history")}
        title={introCardCopy.sendHistory(sentCount)}
        testId="buyer-send-history"
      >
        <DetailSection title="Sent to the buyer">
          <SendHistory submissionId={s.id} kind="IntroEmail" canWrite={canWrite} />
        </DetailSection>
      </CollapsibleCard>
    </div>
  );
}
